import { useContext, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../../Common';
import { getCompanyContactsOptions } from '../../../contact/Contact';
import ValidatedSelect from '../../../common/forms/ValidatedSelect';
import { FormContext } from '../../../common/forms/FormElements';

export const Foreman = ({ customerCompanyId }) => {
    const { values, setFieldValue } = useContext(FormContext);

    // Tracks the company we've already auto-filled for, so auto-fill runs at most
    // once per company selection and a manual clear isn't immediately undone.
    // (Clearing on company change is handled by the company select's onChange.)
    const foremanAutoFilledForCompanyRef = useRef(null);

    // Contact options come from the shared company-contacts lookup, constrained
    // server-side per the tenant's ConstrainContactTypesOnJob setting.
    const { data: contacts, isLoading } = useQuery({
        queryKey: ['jobRequestForemanOptions', customerCompanyId],
        queryFn: () => getCompanyContactsOptions(customerCompanyId, true),
        enabled: Boolean(customerCompanyId),
    });

    // The requestor's own contact id, only when they are themselves a foreman.
    const { data: currentForemanContactId } = useQuery({
        queryKey: ['jobRequestCurrentForeman', customerCompanyId],
        queryFn: () => httpClient.js(ApiRoutes.jobRequests.currentForeman(customerCompanyId)),
        enabled: Boolean(customerCompanyId),
    });

    useEffect(() => {
        // Auto-fill the foreman with the requestor when they are themselves a
        // foreman for this company. Guarded by a per-company ref so clearing the
        // field (or re-renders) doesn't repopulate it.
        if (
            currentForemanContactId
            && foremanAutoFilledForCompanyRef.current !== customerCompanyId
            && !values.foremanId
        ) {
            foremanAutoFilledForCompanyRef.current = customerCompanyId;
            setFieldValue('foremanId', currentForemanContactId);
        }
    }, [currentForemanContactId, customerCompanyId, values.foremanId, setFieldValue]);

    return (
        <ValidatedSelect
            name="foremanId"
            label="Foreman (optional)"
            isLoading={isLoading}
            options={contacts?.companyContactsOptions}
            disabled={!customerCompanyId}
        />
    );
};
